import { jsx, jsxs } from "react/jsx-runtime";
import { node, bool, func, string, oneOf, oneOfType, arrayOf, checkPropTypes } from "prop-types";
import React from "react";
import cx from "classnames";
import headerPropTypeValidation from "../../utils/headerPropTypeValidation";
import AutoFocusOnMount from "../../utils/AutoFocusOnMount";
const propTypes = {
  open: bool,
  closeBtnClick: func,
  children: oneOfType([
    arrayOf(node),
    node
  ]),
  /** Prefix all classes in every element with this string [Inherited] */
  prefix: string,
  /** Additional class name to be added to the root element */
  className: string,
  /** Controls the Sheet's size. Options are: ['small', 'medium', 'large'] */
  size: oneOf(["small", "medium", "large"]),
  /** Controls the Sheet's position. Options are: ['left', 'right'] */
  alignment: oneOf(["left", "right"]),
  /** Affects rendering of sheet on mobile view. Keeps orientation if true */
  preserveAlignment: bool,
  /** Accepts a single ModalHeader component. [Required *] */
  header: headerPropTypeValidation,
  /** Accepts a single ModalFooter component. [Required] */
  footer: (props, propName, componentName) => {
    if (propName === "footer" && props[propName] !== null && props[propName]?.type?.displayName !== "ModalFooter") {
      console.warn(`@ingka/modal: footer propType only accepts ModalFooter component instance or null.
  Leaving the footer propType value as it is may cause the component to not function as intended.`);
    }
    return checkPropTypes({
      footer: oneOfType([
        oneOf([null]).isRequired,
        node
      ])
    }, props, "prop", componentName);
  },
  /** Affects rendering of sheet on mobile view when `preserveAlignment === false`. Makes sheet cover most of the screen. */
  fullSize: bool,
  /** Sets aria-labelledby to Sheets container. Provide either `labelledById ` or `ariaLabel` to ensure the component is accessible [Required] */
  labelledById: string,
  /** Sets aria-label to Sheets container. Provide either `ariaLabel` or `labelledById` to ensure the component is accessible [Required] */
  ariaLabel: string,
  /** Sets aria-describedby to Sheets container */
  describedById: string
};
const Sheets = (props) => {
  const {
    className,
    fullSize,
    prefix = "",
    children,
    open = false,
    closeBtnClick,
    header,
    alignment = "right",
    preserveAlignment = false,
    footer,
    size = "small",
    describedById,
    labelledById,
    ariaLabel,
    ...other
  } = props;
  const classes = cx(
    `${prefix}sheets`,
    `${prefix}sheets--${size}`,
    fullSize && `${prefix}sheets--mobile-full-size`,
    alignment === "left" && `${prefix}sheets--mirrored`,
    preserveAlignment && `${prefix}sheets--preserve-alignment`,
    open ? `${prefix}sheets--enter` : `${prefix}sheets--close`,
    className
  );
  const hookedHeader = header ? React.cloneElement(header, { closeBtnClick, prefix }) : null;
  const hookedFooter = footer && React.cloneElement(footer, { prefix, closeBtnClick }) || null;
  const hookedBody = React.Children.map(children, (child) => child.props ? React.cloneElement(child, { prefix }) : child);
  const [wrapperFocus, contentRef] = AutoFocusOnMount();
  return /* @__PURE__ */ jsxs("div", { "aria-describedby": describedById, "aria-labelledby": labelledById, "aria-label": ariaLabel, ...other, className: classes, "aria-modal": "true", role: "dialog", tabIndex: "-1", children: [
    hookedHeader && !hookedHeader.props.floating && hookedHeader,
    /* @__PURE__ */ jsx("div", { className: `${prefix}sheets__content-wrapper`, role: "document", tabIndex: "0", ref: contentRef, "data-autofocus": wrapperFocus, children: /* @__PURE__ */ jsxs("div", { className: `${prefix}sheet__content`, children: [
      " ",
      hookedHeader && hookedHeader.props.floating && hookedHeader,
      hookedBody
    ] }) }),
    hookedFooter
  ] });
};
Sheets.propTypes = propTypes;
Sheets.displayName = "Sheets";
var Sheets_default = Sheets;
export {
  Sheets_default as default
};
